import { BLOB_LIMITS, CSRF_HEADER, type BlobRef } from "@ide/shared";
import { API_BASE, ApiError, get, post } from "./api";

/**
 * Arquivos binários e grandes do projeto (bancos .db, imagens, zips, logs): o conteúdo vai para o
 * armazenamento do projeto em partes de 4 MB (o envio que cai continua de onde parou) e a árvore
 * do projeto guarda só o sha256 e o tamanho.
 */

export async function sha256Hex(data: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", data as Uint8Array<ArrayBuffer>);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

const base = (projectId: string, sha: string) => `/projects/${projectId}/blobs/${sha}`;

/** Envia o arquivo (se o projeto ainda não tem) e devolve a referência para a árvore. */
export async function uploadBlob(
  projectId: string,
  data: Uint8Array,
  onBytes?: (sent: number) => void,
  knownSha?: string,
): Promise<BlobRef> {
  const sha256 = knownSha ?? (await sha256Hex(data));
  const ref = { sha256, size: data.byteLength };
  const status = await get<{ exists: boolean; complete: boolean; have: number[] }>(
    `${base(projectId, sha256)}/status`,
  );
  if (status.complete) {
    onBytes?.(data.byteLength);
    return ref;
  }
  const { parts } = await post<{ parts: number }>(base(projectId, sha256), { size: data.byteLength });
  const have = new Set(status.have);
  const queue = [...Array(parts).keys()].filter((p) => !have.has(p));
  let sent = (parts - queue.length) * BLOB_LIMITS.partBytes;
  const worker = async () => {
    for (let part = queue.shift(); part !== undefined; part = queue.shift()) {
      const chunk = data.subarray(part * BLOB_LIMITS.partBytes, (part + 1) * BLOB_LIMITS.partBytes);
      await sendPart(`${base(projectId, sha256)}/parts/${part}`, chunk);
      sent += chunk.byteLength;
      onBytes?.(Math.min(sent, data.byteLength));
    }
  };
  await Promise.all(Array.from({ length: Math.min(3, queue.length) }, worker));
  await post(`${base(projectId, sha256)}/complete`);
  return ref;
}

async function sendPart(path: string, chunk: Uint8Array, attempt = 0): Promise<void> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "PUT",
    credentials: "include",
    headers: { "content-type": "application/octet-stream", [CSRF_HEADER]: "1" },
    body: chunk as Uint8Array<ArrayBuffer>,
  }).catch(() => null);
  if (res?.ok) return;
  // Rede instável: tenta de novo algumas vezes antes de desistir.
  if (attempt < 3 && (!res || res.status >= 500 || res.status === 429)) {
    await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
    return sendPart(path, chunk, attempt + 1);
  }
  let message = "Não deu para enviar o arquivo.";
  try {
    message = ((await res?.json()) as { error?: { message?: string } }).error?.message ?? message;
  } catch {
    // resposta sem JSON
  }
  throw new ApiError(res?.status ?? 0, "blob_upload_failed", message);
}

/** Baixa o conteúdo de um arquivo do projeto. */
export async function downloadBlob(projectId: string, ref: BlobRef): Promise<Uint8Array> {
  const res = await fetch(`${API_BASE}${base(projectId, ref.sha256)}`, { credentials: "include" });
  if (!res.ok)
    throw new ApiError(res.status, "blob_download_failed", "Não deu para baixar o arquivo do projeto.");
  return new Uint8Array(await res.arrayBuffer());
}

/** Endereço para baixar (o navegador salva com o nome do arquivo). */
export function blobUrl(projectId: string, ref: BlobRef, name: string) {
  return `${API_BASE}${base(projectId, ref.sha256)}?name=${encodeURIComponent(name)}`;
}
